import React from 'react';
import { sparklineBounds } from './sparklineScale';

/** Inline SVG sparkline; green when the series trends up, red when down. */
export default function ClientReportingSparkline({
  values,
  /** ISO dates parallel to `values`. Omit to fall back to even index spacing. */
  dates = null,
  positive,
  wide = false,
  className = '',
}) {
  if (!values || values.length < 2) {
    return <span className="vd-ml-sparkline-empty">—</span>;
  }

  const width = wide ? 280 : 120;
  const height = wide ? 56 : 34;
  const pad = 2;
  // Floored so a fraction-of-a-percent move does not render as a cliff purely
  // because min/max autoscaling fills the box. See sparklineScale.
  const { min, range } = sparklineBounds(values);

  /**
   * X position by DATE when dates are supplied, falling back to index.
   *
   * Index spacing draws every point an equal distance apart, so an account with
   * two trades on one day and nothing for the next fortnight rendered as a long
   * even ramp — a shape the main chart, which plots on a real time axis, never
   * produced for the same account. Gaps have to read as gaps.
   */
  const times = (dates || [])
    .map((d) => new Date(d).getTime())
    .filter((t) => Number.isFinite(t));
  const useDates = times.length === values.length && times.length > 1;
  const firstT = useDates ? times[0] : 0;
  const spanT = useDates ? (times[times.length - 1] - firstT) : 0;

  const points = values.map((v, i) => {
    const frac = useDates && spanT > 0
      ? (times[i] - firstT) / spanT
      : i / (values.length - 1);
    const x = pad + frac * (width - pad * 2);
    const y = height - pad - ((v - min) / range) * (height - pad * 2);
    return [x, y];
  });

  const line = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${points[points.length - 1][0].toFixed(1)},${height} L${points[0][0].toFixed(1)},${height} Z`;
  const stroke = positive ? '#4ade80' : '#f87171';
  const fill = positive ? 'rgba(74, 222, 128, 0.18)' : 'rgba(248, 113, 113, 0.18)';

  return (
    <svg
      className={`vd-ml-sparkline${wide ? ' vd-ml-sparkline-wide' : ''} ${className}`.trim()}
      width={wide ? '100%' : width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path d={area} fill={fill} stroke="none" />
      <path d={line} fill="none" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
